import { Upload3dDataDropHandler } from "@/components/common/upload-3d-data-drop-handler";
import { useActiveCamera } from "@/components/r3f/cameras/active-camera-context";
import { DataSessionsDropDown } from "@/components/ui/data-sessions-dropdown";
import { ToolsHelpBanners } from "@/components/ui/help-banners/tools-help-banners";
import { OverviewModeSceneFilterToggle } from "@/components/ui/overview-mode-scene-filter-toggle";
import { SceneContextMenu } from "@/components/ui/scene-context-menu";
import { useCurrentArea, useCurrentScene } from "@/modes/mode-data-context";
import { useWalkOverlayResponsiveLayout } from "@/modes/walk-mode/walk-overlay-responsive-layout";
import {
  selectIElementsSlice,
  type OnlyIElementsStore,
} from "@/store/ielements-slice-selector";
import { selectFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import { curryAppSelector } from "@/store/reselect";
import { setActiveElement } from "@/store/selections-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { SceneFilter } from "@/types/scene-filter";
import { isIElementValidPointCloudStream } from "@/types/type-guards";
import { CameraViewType } from "@/utils/cam-view";
import type { GUID } from "@faro-lotv/foundation";
import {
  isIElementGaussianSplats,
  type IElement,
} from "@faro-lotv/ielement-types";
import {
  selectChildDepthFirst,
  selectIElement,
} from "@faro-lotv/project-source";
import { Stack } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { useCallback, useEffect, useMemo, type JSX } from "react";
import { ExportModeTab } from "../export-mode/export-mode-types";
import { moveToExportMode } from "../export-mode/export-mode-utils";
import { selectIsPointCloudViewable } from "../select-current-area-data";

/**
 * @returns the Overlay for OverviewMode
 */
export function OverviewOverlay(): JSX.Element | null {
  const dispatch = useAppDispatch();

  const activeTool = useAppSelector(selectActiveTool);

  // Get data from store
  const { dataSessions3d, dataSessions5d } = useCurrentArea();
  const { main, referenceElement, cad } = useCurrentScene();

  const options = useMemo(
    () => [
      ...dataSessions3d.pointClouds,
      ...dataSessions3d.gaussianSplats,
      ...dataSessions5d,
    ],
    [dataSessions3d, dataSessions5d],
  );

  const { setCameraProjection } = useActiveCamera();
  const sceneFilter = useAppSelector(selectFlyWalkSceneFilter);
  // Gaussian Splats do not support orthographic projection
  // therefore, the camera projection is set to perspective when viewing Gaussian Splats
  useEffect(() => {
    if (sceneFilter === SceneFilter.GaussianSplats) {
      setCameraProjection(CameraViewType.perspective);
    }
  }, [sceneFilter, setCameraProjection]);

  useEffect(() => {
    if (activeTool === ToolName.export) {
      moveToExportMode(dispatch, ExportModeTab.pointCloud, true);
    }
  }, [dispatch, activeTool]);

  const { getState } = useAppStore();
  const onDataSessionChanged = useCallback(
    (id: GUID) => {
      const state = getState();
      const element = selectIElement(id)(state);
      const model = selectChildDepthFirst(element, (element) =>
        selectIsViewableRealityCapture(element)(state),
      )(state);
      if (model) {
        dispatch(setActiveElement(model.id));
      } else {
        dispatch(setActiveElement(id));
      }
    },
    [dispatch, getState],
  );

  const { buttonLayout, overlayRef } = useWalkOverlayResponsiveLayout();

  const model = main && referenceElement ? main : cad;

  return (
    <Upload3dDataDropHandler>
      <Stack direction="column" sx={{ gap: 2, width: "100%", p: 2 }}>
        <Stack
          direction="row"
          ref={overlayRef}
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            // Use gap instead of spacing prop so it works correctly with flex-wrapped elements
            gap: 1,
            width: "100%",
          }}
        >
          <Stack
            direction="row"
            sx={{
              gap: 1.25,
              overflow: "hidden",
            }}
          >
            <DataSessionsDropDown
              datasets={options}
              referenceElement={referenceElement}
              onDataSessionChanged={onDataSessionChanged}
            />
          </Stack>
          {model && <OverviewModeSceneFilterToggle layout={buttonLayout} />}
        </Stack>
        <ToolsHelpBanners />
      </Stack>
      <SceneContextMenu />
    </Upload3dDataDropHandler>
  );
}

const selectIsViewableRealityCapture = curryAppSelector(
  createSelector(
    [selectIElementsSlice, (state: OnlyIElementsStore, e: IElement) => e],
    (iElements, element) =>
      isIElementGaussianSplats(element) ||
      (isIElementValidPointCloudStream(element) &&
        selectIsPointCloudViewable(element)({ iElements })),
  ),
);
